// src/store/modules/uploadQueue.js
// Vuex module for tracking background file uploads with progress

const state = () => ({
  uploads: new Map(), // Map<string, { progress: number|null, status: string, error: string|null, filename: string }>
  uploadCounter: 0, // For generating unique IDs
});

const getters = {
  hasActiveUploads: (state) => {
    for (const [, upload] of state.uploads) {
      if (upload.status === 'uploading' || upload.status === 'pending') {
        return true;
      }
    }
    return false;
  },
  
  activeUploadCount: (state) => {
    let count = 0;
    for (const [, upload] of state.uploads) {
      if (upload.status === 'uploading' || upload.status === 'pending') {
        count++;
      }
    }
    return count;
  },
  
  /** Returns overall progress (0-100) across all active uploads, or null if none active */
  overallProgress: (state) => {
    let total = 0;
    let count = 0;
    for (const [, upload] of state.uploads) {
      if (upload.status === 'uploading' || upload.status === 'pending') {
        // If any active upload does not have a progress value yet, render indeterminate
        if (upload.progress === null || upload.progress === undefined) {
          return null;
        }
        total += upload.progress;
        count++;
      }
    }
    if (count === 0) return null;
    return Math.round(total / count);
  },
  
  /** Get all uploads as an array (for debugging/display) */
  allUploads: (state) => Array.from(state.uploads.entries()).map(([id, data]) => ({ id, ...data })),
};

const mutations = {
  START_UPLOAD(state, { id, filename }) {
    state.uploads.set(id, { progress: null, status: 'pending', error: null, filename });
    // Force reactivity
    state.uploads = new Map(state.uploads);
  },
  
  UPDATE_PROGRESS(state, { id, progress }) {
    const upload = state.uploads.get(id);
    if (upload) {
      upload.progress = progress;
      upload.status = 'uploading';
      state.uploads = new Map(state.uploads);
    }
  },
  
  FINISH_UPLOAD(state, { id }) {
    const upload = state.uploads.get(id);
    if (upload) {
      upload.progress = 100;
      upload.status = 'completed';
      state.uploads = new Map(state.uploads);
    }
  },
  
  FAIL_UPLOAD(state, { id, error }) {
    const upload = state.uploads.get(id);
    if (upload) {
      upload.status = 'failed';
      upload.error = error || 'Upload failed';
      state.uploads = new Map(state.uploads);
    }
  },
  
  REMOVE_UPLOAD(state, { id }) {
    state.uploads.delete(id);
    state.uploads = new Map(state.uploads);
  },
  
  INCREMENT_COUNTER(state) {
    state.uploadCounter++;
  },
  
  CLEAR_COMPLETED(state) {
    for (const [id, upload] of state.uploads) {
      if (upload.status === 'completed' || upload.status === 'failed') {
        state.uploads.delete(id);
      }
    }
    state.uploads = new Map(state.uploads);
  },
};

const actions = {
  /**
   * Register a new upload job. Returns the upload ID.
   * @param {string} filename - Name of the file being uploaded
   * @returns {string} Upload ID
   */
  startUpload({ commit, state }, { filename }) {
    commit('INCREMENT_COUNTER');
    const id = `upload_${Date.now()}_${state.uploadCounter}`;
    commit('START_UPLOAD', { id, filename });
    return id;
  },
  
  /**
   * Update progress for an ongoing upload
   * @param {string} id - Upload ID
   * @param {number} progress - Progress value 0-100
   */
  updateProgress({ commit }, { id, progress }) {
    commit('UPDATE_PROGRESS', { id, progress: Math.round(progress) });
  },
  
  /**
   * Mark an upload as successfully completed
   * @param {string} id - Upload ID
   */
  finishUpload({ commit, state }, { id }) {
    const upload = state.uploads.get(id);
    const filename = upload?.filename || 'Upload';
    commit('FINISH_UPLOAD', { id });
    
    // Show success toast
    try {
      const { toast } = require('vue3-toastify');
      toast.success(`${filename} uploaded successfully!`, { autoClose: 3000 });
    } catch (e) {
      console.log('[uploadQueue] Toast not available:', e);
    }
    
    // Auto-remove after delay
    setTimeout(() => {
      commit('REMOVE_UPLOAD', { id });
    }, 3000);
  },
  
  /**
   * Mark an upload as failed
   * @param {string} id - Upload ID
   * @param {string} error - Error message
   */
  failUpload({ commit, state }, { id, error }) {
    const upload = state.uploads.get(id);
    const filename = upload?.filename || 'Upload';
    commit('FAIL_UPLOAD', { id, error });
    
    // Show error toast
    try {
      const { toast } = require('vue3-toastify');
      toast.error(`${filename} failed: ${error}`, { autoClose: 5000 });
    } catch (e) {
      console.log('[uploadQueue] Toast not available:', e);
    }
  },
  
  /**
   * Remove a specific upload from tracking
   * @param {string} id - Upload ID
   */
  removeUpload({ commit }, { id }) {
    commit('REMOVE_UPLOAD', { id });
  },
  
  /**
   * Clear all completed/failed uploads
   */
  clearCompleted({ commit }) {
    commit('CLEAR_COMPLETED');
  },
};

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
};
